import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Mail, CheckCircle2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SubscribeWidget({ variant = 'default' }) {
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone: '' });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim() || !form.last_name.trim() || !form.email.trim()) {
      toast.error('First name, last name, and email are required');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
      };
      const res = await axios.post(`${API_URL}/api/subscribers`, payload);
      toast.success(res.data.message || 'Subscribed!');
      setSuccess(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not subscribe. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Compact footer variant
  const isFooter = variant === 'footer';

  if (success) {
    return (
      <div
        className={`${isFooter ? 'bg-[#1E293B] text-white' : 'bg-white border border-[#E2E8F0]'} rounded-lg p-5 text-center`}
        data-testid="subscribe-success"
      >
        <CheckCircle2 size={28} className={`${isFooter ? 'text-[#FB923C]' : 'text-green-600'} mx-auto mb-2`} />
        <div className={`font-bold text-base ${isFooter ? 'text-white' : 'text-[#0A0F1C]'}`}>You are subscribed</div>
        <p className={`text-xs mt-1 ${isFooter ? 'text-[#94A3B8]' : 'text-[#475569]'}`}>
          Check your inbox for a welcome email.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className={`${isFooter ? '' : 'bg-white border border-[#E2E8F0] rounded-lg p-5'}`}
      data-testid="subscribe-widget"
    >
      {!isFooter && (
        <div className="mb-3">
          <div className="flex items-center gap-2 text-[#1D4ED8] mb-1">
            <Mail size={16} />
            <span className="text-xs font-bold uppercase tracking-wider">Stay in the loop</span>
          </div>
          <h3 className="text-lg font-bold text-[#0A0F1C]">Subscribe for emails, resources, and support</h3>
          <p className="text-xs text-[#475569] mt-1">
            Direct from David Ashley. Practical tools, free resources, and updates - no spam.
          </p>
        </div>
      )}
      {isFooter ? (
        <div className="space-y-2.5">
          <div className="grid grid-cols-2 gap-2">
            <Input
              placeholder="First name"
              value={form.first_name}
              onChange={(e) => setForm({ ...form, first_name: e.target.value })}
              required
              className="h-11 bg-white/5 border-white/15 text-white placeholder-white/45 focus-visible:ring-[#F97316] focus-visible:border-[#F97316] rounded-lg"
              data-testid="subscribe-first-name"
            />
            <Input
              placeholder="Last name"
              value={form.last_name}
              onChange={(e) => setForm({ ...form, last_name: e.target.value })}
              required
              className="h-11 bg-white/5 border-white/15 text-white placeholder-white/45 focus-visible:ring-[#F97316] focus-visible:border-[#F97316] rounded-lg"
              data-testid="subscribe-last-name"
            />
          </div>
          <div className="relative">
            <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
            <Input
              type="email"
              placeholder="Email address"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              required
              className="h-11 pl-9 bg-white/5 border-white/15 text-white placeholder-white/45 focus-visible:ring-[#F97316] focus-visible:border-[#F97316] rounded-lg"
              data-testid="subscribe-email"
            />
          </div>
          <Input
            type="tel"
            placeholder="Phone (optional)"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            className="h-11 bg-white/5 border-white/15 text-white placeholder-white/45 focus-visible:ring-[#F97316] focus-visible:border-[#F97316] rounded-lg"
            data-testid="subscribe-phone"
          />
          <Button
            type="submit"
            disabled={submitting}
            className="w-full h-11 bg-[#F97316] hover:bg-[#FB923C] text-white font-bold rounded-lg"
            data-testid="subscribe-submit"
          >
            {submitting ? 'Subscribing...' : 'Subscribe'}
          </Button>
          <p className="text-[10px] text-white/45 leading-relaxed pt-1">
            No spam. Unsubscribe with one click. We use your details only to send the newsletter and occasional resource updates.
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
            <Input placeholder="First name" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} required data-testid="subscribe-first-name" />
            <Input placeholder="Last name" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} required data-testid="subscribe-last-name" />
          </div>
          <Input type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="mb-2" data-testid="subscribe-email" />
          <Input type="tel" placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mb-3" data-testid="subscribe-phone" />
          <Button type="submit" disabled={submitting} className="w-full btn-primary-blue font-bold py-2.5 rounded-lg" data-testid="subscribe-submit">
            {submitting ? 'Subscribing...' : 'Subscribe'}
          </Button>
        </>
      )}
    </form>
  );
}
